/**
 * ImpersonationBanner — appears at the very top of every page when the
 * current session was minted via superadmin impersonation. One-click
 * "Return to your account" swaps the session cookie back and reloads.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCog, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const imp = user?._impersonation;
  if (!imp?.active) return null;

  const exit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/impersonate/exit");
      toast.success("Restored your superadmin session");
      await refresh();
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not exit impersonation");
      setBusy(false);
    }
  };

  const by = imp.by || {};
  const expiresAt = imp.session_expires_at ? new Date(imp.session_expires_at) : null;
  const minutesLeft = expiresAt ? Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60000)) : null;

  return (
    <div
      className="w-full bg-red-600 text-white text-sm px-4 py-2 flex items-center gap-3 justify-center flex-wrap sticky top-0 z-[80] shadow-md"
      data-testid="impersonation-banner"
    >
      <UserCog className="h-4 w-4 shrink-0" />
      <span className="font-medium">
        You&apos;re acting as <strong>{user.name}</strong> ({user.email})
      </span>
      <span className="text-white/80 text-xs">
        · initiated by {by.name || by.user_id || "superadmin"}
        {minutesLeft !== null && ` · ${minutesLeft}m left`}
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={busy}
        className="ml-2 inline-flex items-center gap-1 rounded-full bg-white text-red-700 hover:bg-white/90 px-3 py-1 text-xs font-semibold disabled:opacity-70"
        data-testid="impersonation-exit-btn"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
        Return to your account
      </button>
    </div>
  );
}
